"use client"

import type React from "react";
import { cn } from "@repo/ui/lib/utils";

interface PageHeaderProps {
  children: React.ReactNode;
  className?: string;
}

interface PageTitleProps {
  children: React.ReactNode;
  className?: string;
}

interface PageDescriptionProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * PageHeader
 * 
 * Composant standardisé pour les en-têtes de page avec titre et description.
 * 
 * @example
 * ```tsx
 * <PageHeader>
 *   <PageHeader.Title>Titre de la page</PageHeader.Title>
 *   <PageHeader.Description>Description de la page</PageHeader.Description>
 * </PageHeader>
 * ```
 */
function PageHeaderRoot({ children, className }: PageHeaderProps) {
  return (
    <div className={cn("space-y-1 mb-4", className)}>
      {children}
    </div>
  );
}

/**
 * PageTitle
 * 
 * Titre de la page avec style standardisé.
 */
function PageTitle({ children, className }: PageTitleProps) {
  return (
    <h1 className={cn(
      "text-xl font-semibold tracking-tight text-foreground",
      "sm:text-2xl",
      className
    )}>
      {children}
    </h1>
  );
}

/**
 * PageDescription
 * 
 * Description de la page avec style standardisé.
 */
function PageDescription({ children, className }: PageDescriptionProps) {
  return (
    <p className={cn(
      "text-sm text-muted-foreground",
      className
    )}>
      {children}
    </p>
  );
}

// Export with nested components
export const PageHeader = Object.assign(PageHeaderRoot, {
  Title: PageTitle,
  Description: PageDescription,
});

// Also export individually for convenience
export { PageTitle, PageDescription };

